import { useEffect, useState } from 'react';
import { Users, Download, Search, TrendingUp, Send, Loader2, PhoneCall, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { analytics, API_BASE } from '../lib/api';

/** Admin Analytics — users list, daily visits, CSV export, and the
 *  "Missing You" 7-day inactive retention picker. */
export default function AdminAnalytics() {
  const [tab, setTab] = useState('users');

  return (
    <div data-testid="admin-analytics">
      <div className="flex gap-2 mb-4 flex-wrap">
        {[
          { id: 'users', label: 'Users', Icon: Users },
          { id: 'visits', label: 'Daily visits', Icon: TrendingUp },
          { id: 'missing', label: '"Missing You" (7d idle)', Icon: Send },
        ].map(t => (
          <button
            key={t.id}
            data-testid={`analytics-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 h-9 text-xs font-black uppercase tracking-wider transition ${
              tab === t.id ? 'bg-ink text-white' : 'bg-white border-2 border-ink/15 text-ink/70 hover:border-ink'
            }`}
          >
            <t.Icon className="w-3.5 h-3.5"/>{t.label}
          </button>
        ))}
        <a
          data-testid="analytics-export-users"
          href={`${API_BASE.replace(/\/api\/?$/, '')}${new URL(analytics.csvUrl('users')).pathname}`}
          className="ml-auto inline-flex items-center gap-1 rounded-lg border-2 border-[#0D47A1] text-[#0D47A1] hover:bg-[#E3F2FD] font-black uppercase text-xs tracking-wider h-9 px-3"
        >
          <Download className="w-3.5 h-3.5"/>Users CSV
        </a>
        <a
          data-testid="analytics-export-activity"
          href={`${API_BASE.replace(/\/api\/?$/, '')}${new URL(analytics.csvUrl('activity')).pathname}`}
          className="inline-flex items-center gap-1 rounded-lg border-2 border-ink/20 text-ink hover:border-ink font-black uppercase text-xs tracking-wider h-9 px-3"
        >
          <Download className="w-3.5 h-3.5"/>Activity CSV
        </a>
      </div>

      {tab === 'users' && <UsersPanel/>}
      {tab === 'visits' && <VisitsPanel/>}
      {tab === 'missing' && <MissingYouPanel/>}
    </div>
  );
}

function UsersPanel() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  const load = (query) => {
    setLoading(true);
    analytics.users(query || undefined)
      .then((d) => setRows(d.users || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(''); }, []);

  return (
    <div>
      <div className="mb-3 flex items-center gap-2 max-w-md rounded-lg border-2 border-ink/15 bg-white px-3">
        <Search className="w-4 h-4 text-ink/50"/>
        <input
          data-testid="analytics-user-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load(q)}
          placeholder="Search by name or phone"
          className="flex-1 py-2 text-sm outline-none bg-transparent"
        />
      </div>
      {loading && <div className="text-ink/50 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div>}
      <div className="rounded-lg border border-ink/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-muted text-[10px] uppercase tracking-wider font-black text-ink/60">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Phone</th>
              <th className="text-left px-3 py-2">Role</th>
              <th className="text-right px-3 py-2">Logins</th>
              <th className="text-right px-3 py-2">Visits</th>
              <th className="text-left px-3 py-2">Last seen</th>
              <th className="text-left px-3 py-2">Signup</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} data-testid={`analytics-user-${u.id}`} className="border-t border-ink/5 hover:bg-muted/50">
                <td className="px-3 py-2 font-bold text-ink truncate max-w-[180px]">{u.name || '—'}</td>
                <td className="px-3 py-2 font-mono">{u.phone}</td>
                <td className="px-3 py-2 uppercase text-[11px] tracking-wider font-black text-ink/60">{u.role}</td>
                <td className="px-3 py-2 text-right font-mono">{u.login_count}</td>
                <td className="px-3 py-2 text-right font-mono">{u.visit_count}</td>
                <td className="px-3 py-2 text-[11px] text-ink/60">{u.last_seen_at ? new Date(u.last_seen_at).toLocaleString() : '—'}</td>
                <td className="px-3 py-2 text-[11px] text-ink/60">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VisitsPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { analytics.daily(14).then(setData); }, []);
  if (!data) return <div className="text-ink/50">Loading…</div>;
  const max = Math.max(1, ...data.series.map(r => r.total_visits));
  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="rounded-lg bg-muted p-3">
          <div className="text-[10px] uppercase tracking-[0.2em] font-black text-ink/60">Last {data.totals.days} days</div>
          <div className="font-heading font-black text-2xl mt-0.5">{data.totals.total_visits}</div>
          <div className="text-[11px] text-ink/60">total page visits</div>
        </div>
        <div className="rounded-lg bg-[#F1F8E9] border border-[#A5D6A7] p-3">
          <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[#1B5E20]">Unique visitors</div>
          <div className="font-heading font-black text-2xl mt-0.5 text-[#1B5E20]">{data.totals.unique_visitors}</div>
          <div className="text-[11px] text-ink/60">across the same window</div>
        </div>
        <div className="rounded-lg bg-white border border-ink/10 p-3">
          <div className="text-[10px] uppercase tracking-[0.2em] font-black text-ink/60">Avg / day</div>
          <div className="font-heading font-black text-2xl mt-0.5">{data.series.length ? Math.round(data.totals.total_visits / data.series.length) : 0}</div>
        </div>
      </div>
      <div className="rounded-lg border border-ink/10 p-3">
        <div className="text-[10px] uppercase tracking-[0.2em] font-black text-ink/60 mb-2">Visits per day</div>
        <div className="flex items-end gap-1 h-32">
          {data.series.map(r => (
            <div key={r.date} data-testid={`visits-bar-${r.date}`} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full bg-[#0D47A1] rounded-t" style={{ height: `${(r.total_visits / max) * 100}%`, minHeight: 2 }} title={`${r.date}: ${r.total_visits}`}/>
              <span className="text-[9px] font-mono text-ink/50 -rotate-45 origin-top-left translate-x-2">{r.date.slice(5)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MissingYouPanel() {
  const [days, setDays] = useState(7);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    fetch(`${API_BASE}/admin/customer-retention/candidates?days_inactive=${days}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('mandispot_token')}` },
    })
      .then(r => r.json())
      .then((d) => setRows(d.candidates || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  const markSent = async (row) => {
    try {
      await fetch(`${API_BASE}/admin/customer-retention/mark-sent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('mandispot_token')}` },
        body: JSON.stringify({
          user_id: row.user_id,
          store_id: row.favourite_store?.id,
          message: row.message,
          promo_code: row.promo?.code,
        }),
      });
      toast.success('Marked as sent — audit trail written');
    } catch { /* best-effort */ }
  };

  return (
    <div>
      <div className="mb-3 flex items-center gap-2 text-xs">
        <span className="text-ink/60">Show customers idle for</span>
        <select
          data-testid="missing-days-select"
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
          className="rounded-lg border-2 border-ink/15 bg-white px-2 py-1 font-black text-ink"
        >
          <option value={3}>3 days</option>
          <option value={7}>7 days</option>
          <option value={14}>14 days</option>
          <option value={30}>30 days</option>
        </select>
        <span className="text-ink/60">— we&apos;ve built a personal WhatsApp message from their favourite shop, tap Send to open WhatsApp.</span>
      </div>
      {loading ? (
        <div className="text-ink/50 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-ink/20 p-8 text-center text-sm text-ink/60">
          Everyone&apos;s active. Nothing to nudge.
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map(r => (
            <li key={r.user_id} data-testid={`missing-row-${r.user_id}`} className="rounded-xl border-2 border-ink/10 p-3 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="font-black text-ink text-sm truncate">
                  {r.name || 'Villager'} <span className="ml-1 font-mono text-ink/50">+{r.phone}</span>
                </div>
                <div className="text-[11px] text-ink/60">
                  Last seen {r.last_seen_at ? new Date(r.last_seen_at).toLocaleDateString() : 'never'} · {r.login_count} logins
                  {r.favourite_store && <> · fav shop <b>{r.favourite_store.name}</b> ({r.favourite_store.orders_last_90d} orders)</>}
                </div>
                {r.promo && (
                  <div className="text-[11px] text-[#B26900] font-bold mt-0.5">
                    <Sparkles className="w-3 h-3 inline"/> Promo: {r.promo.code} · {r.promo.discount_pct ? `${r.promo.discount_pct}% off` : `₹${r.promo.discount_flat} off`}
                  </div>
                )}
              </div>
              {r.wa_link ? (
                <a
                  data-testid={`missing-wa-${r.user_id}`}
                  href={r.wa_link}
                  target="_blank" rel="noreferrer"
                  onClick={() => markSent(r)}
                  className="inline-flex items-center gap-1 rounded-lg bg-[#25D366] hover:bg-[#128C7E] text-white font-black uppercase text-xs tracking-wider h-9 px-3"
                >
                  <PhoneCall className="w-3.5 h-3.5"/>Send WhatsApp
                </a>
              ) : (
                <span className="text-[10px] uppercase tracking-wider font-black text-ink/40">No phone</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
